// src/context/DiscoveryJobContext.tsx
/**
 * App-level context for tracking discovery jobs across navigation.
 *
 * Persists job IDs in localStorage (per account) and reconnects to
 * SSE streams on mount so progress survives page navigation and refresh.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
  useMemo,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { notifications } from "@mantine/notifications";
import {
  type DiscoveryJobResponse,
  getActiveDiscoveryJobs,
  getDiscoveryJobStatus,
  cancelDiscoveryJob,
  streamDiscoveryProgress,
} from "@/api/discovery";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DiscoveryJobState {
  jobId: string;
  dataSourceId: string | null;
  status: DiscoveryJobResponse["status"];
  progressPct: number;
  progressMessage: string;
  tablesCompleted: number;
  tablesTotal: number;
  proposalId: string | null;
  error: string | null;
}

interface DiscoveryJobContextValue {
  jobs: Record<string, DiscoveryJobState>;
  isInitializing: boolean;

  trackJob(jobId: string, dataSourceId: string): void;
  cancelJob(jobId: string): Promise<void>;
  dismissJob(jobId: string): void;

  activeJobs: DiscoveryJobState[];
  completedJobs: DiscoveryJobState[];
  hasActiveJobs: boolean;

  isSetupSurfaceActive: boolean;
  setSetupSurfaceActive(active: boolean): void;
  getJobForDataSource(dataSourceId: string): DiscoveryJobState | null;
}

// The backend classifies failures to customer-safe copy at the write boundary
// (services/discovery/errors.py), so error_message is safe to show as-is. This
// only covers the case where the field is absent — never a raw exception.
const DISCOVERY_FAILED_FALLBACK =
  "Something went wrong while analyzing this data source. Our team has been notified.";

const DiscoveryJobContext = createContext<DiscoveryJobContextValue | null>(null);

// ---------------------------------------------------------------------------
// localStorage helpers (per-account scoped)
// ---------------------------------------------------------------------------

function storageKey(accountId: string): string {
  return `verity:discovery-jobs:${accountId}`;
}

function readStoredJobIds(accountId: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey(accountId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredJobIds(accountId: string, jobIds: string[]): void {
  if (jobIds.length === 0) {
    localStorage.removeItem(storageKey(accountId));
  } else {
    localStorage.setItem(storageKey(accountId), JSON.stringify(jobIds));
  }
}

function addStoredJobId(accountId: string, jobId: string): void {
  const ids = readStoredJobIds(accountId);
  if (!ids.includes(jobId)) {
    writeStoredJobIds(accountId, [...ids, jobId]);
  }
}

function removeStoredJobId(accountId: string, jobId: string): void {
  const ids = readStoredJobIds(accountId);
  writeStoredJobIds(
    accountId,
    ids.filter((id) => id !== jobId)
  );
}

// Dismissed jobs — persist so they don't reappear after refresh
function dismissedKey(accountId: string): string {
  return `verity:discovery-dismissed:${accountId}`;
}

function readDismissedJobIds(accountId: string): string[] {
  try {
    const raw = localStorage.getItem(dismissedKey(accountId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addDismissedJobId(accountId: string, jobId: string): void {
  const ids = readDismissedJobIds(accountId);
  if (!ids.includes(jobId)) {
    localStorage.setItem(dismissedKey(accountId), JSON.stringify([...ids, jobId]));
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const DiscoveryJobProvider = ({ children }: { children: ReactNode }) => {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.id ?? null;

  const [jobs, setJobs] = useState<Record<string, DiscoveryJobState>>({});
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSetupSurfaceActive, setSetupSurfaceActive] = useState(false);

  // Track SSE cleanup functions per jobId
  const sseCleanupRef = useRef<Record<string, () => void>>({});
  const prevAccountIdRef = useRef<string | null>(null);
  const isSetupSurfaceActiveRef = useRef(isSetupSurfaceActive);
  isSetupSurfaceActiveRef.current = isSetupSurfaceActive;

  // -------------------------------------------------------------------
  // SSE management
  // -------------------------------------------------------------------

  const closeSSE = useCallback((jobId: string) => {
    sseCleanupRef.current[jobId]?.();
    delete sseCleanupRef.current[jobId];
  }, []);

  const closeAllSSE = useCallback(() => {
    for (const jobId of Object.keys(sseCleanupRef.current)) {
      sseCleanupRef.current[jobId]?.();
    }
    sseCleanupRef.current = {};
  }, []);

  const startSSE = useCallback(
    (jobId: string) => {
      // Don't double-connect
      if (sseCleanupRef.current[jobId]) return;

      const cleanup = streamDiscoveryProgress(
        jobId,
        (event) => {
          if (event.type === "progress") {
            setJobs((prev) => {
              const existing = prev[jobId];
              if (!existing) return prev;
              return {
                ...prev,
                [jobId]: {
                  ...existing,
                  status: "running",
                  progressPct: event.pct ?? existing.progressPct,
                  tablesCompleted:
                    event.tables_completed ?? existing.tablesCompleted,
                  tablesTotal: event.tables_total ?? existing.tablesTotal,
                  progressMessage: event.message ?? existing.progressMessage,
                },
              };
            });
          } else if (event.type === "completed") {
            closeSSE(jobId);
            setJobs((prev) => {
              const existing = prev[jobId];
              if (!existing) return prev;
              return {
                ...prev,
                [jobId]: {
                  ...existing,
                  status: "completed",
                  progressPct: 100,
                  proposalId: event.proposal_id ?? null,
                },
              };
            });
            if (!isSetupSurfaceActiveRef.current) {
              notifications.show({
                title: "Discovery complete",
                message:
                  "Your semantic package proposal is ready for review.",
                color: "green",
                autoClose: 10000,
              });
            }
          } else if (event.type === "failed") {
            closeSSE(jobId);
            if (accountId) removeStoredJobId(accountId, jobId);
            setJobs((prev) => {
              const existing = prev[jobId];
              if (!existing) return prev;
              return {
                ...prev,
                [jobId]: {
                  ...existing,
                  status: "failed",
                  error: event.error_message ?? DISCOVERY_FAILED_FALLBACK,
                },
              };
            });
            if (!isSetupSurfaceActiveRef.current) {
              notifications.show({
                title: "Discovery failed",
                message: event.error_message ?? DISCOVERY_FAILED_FALLBACK,
                color: "red",
                autoClose: 10000,
              });
            }
          } else if (event.type === "cancelled") {
            closeSSE(jobId);
            if (accountId) removeStoredJobId(accountId, jobId);
            setJobs((prev) => {
              const existing = prev[jobId];
              if (!existing) return prev;
              return {
                ...prev,
                [jobId]: { ...existing, status: "cancelled" },
              };
            });
          } else if (event.type === "timeout") {
            // The backend caps how long a progress stream stays open. The job
            // itself may well still be running, so the status is deliberately
            // left alone — we stop listening and tell the user how to catch up.
            // Without this branch the stream would just go quiet and the
            // progress bar would sit there looking stuck.
            closeSSE(jobId);
            if (!isSetupSurfaceActiveRef.current) {
              notifications.show({
                title: "Still working",
                message:
                  event.error_message ??
                  "This is taking longer than expected. Refresh the page to see the latest status.",
                color: "blue",
                autoClose: 10000,
              });
            }
          }
        },
        () => {
          // SSE error — will fall back to one-off poll on next mount
          closeSSE(jobId);
        }
      );

      sseCleanupRef.current[jobId] = cleanup;
    },
    [accountId, closeSSE]
  );

  // -------------------------------------------------------------------
  // Initialization: recover jobs from localStorage + /active endpoint
  // -------------------------------------------------------------------

  useEffect(() => {
    if (!accountId) {
      // Logged out or no account — reset
      closeAllSSE();
      setJobs({});
      setIsInitializing(false);
      return;
    }

    // Account switch — reset
    if (prevAccountIdRef.current && prevAccountIdRef.current !== accountId) {
      closeAllSSE();
      setJobs({});
    }
    prevAccountIdRef.current = accountId;

    let cancelled = false;

    const init = async () => {
      setIsInitializing(true);

      // 1. Merge localStorage + /active endpoint, filtering out dismissed jobs
      const localIds = readStoredJobIds(accountId);
      const dismissedIds = new Set(readDismissedJobIds(accountId));
      let serverJobs: DiscoveryJobResponse[] = [];

      try {
        serverJobs = await getActiveDiscoveryJobs();
      } catch {
        // Graceful fallback — endpoint may not be deployed yet
      }

      if (cancelled) return;

      const serverJobMap = new Map(
        serverJobs.map((j) => [j.job_id, j])
      );
      const allJobIds = [
        ...new Set([...localIds, ...serverJobs.map((j) => j.job_id)]),
      ].filter((id) => !dismissedIds.has(id));

      // 2. Fetch status for each job (use server data if available)
      const newJobs: Record<string, DiscoveryJobState> = {};
      const validJobIds: string[] = [];

      await Promise.all(
        allJobIds.map(async (jobId) => {
          const fromServer = serverJobMap.has(jobId);
          let jobData = serverJobMap.get(jobId);
          if (!jobData) {
            try {
              jobData = await getDiscoveryJobStatus(jobId);
            } catch {
              // 404 or error — stale job, skip
              return;
            }
          }
          if (cancelled) return;

          // Skip completed jobs that the server didn't return — their
          // proposal is no longer needs_review (approved/rejected/expired),
          // so they should not be shown or resumed.
          if (!fromServer && jobData.status === "completed") {
            return;
          }

          newJobs[jobId] = {
            jobId: jobData.job_id,
            dataSourceId: jobData.data_source_id ?? null,
            status: jobData.status,
            progressPct: jobData.progress_pct,
            progressMessage: jobData.progress_message ?? "",
            tablesCompleted: jobData.tables_completed,
            tablesTotal: jobData.tables_total,
            proposalId: jobData.result_proposal_id ?? null,
            error: jobData.error_message ?? null,
          };

          if (
            jobData.status === "pending" ||
            jobData.status === "running"
          ) {
            validJobIds.push(jobId);
          }
        })
      );

      if (cancelled) return;

      // 3. Sync localStorage — only keep valid (non-404) job IDs
      const idsToStore = Object.keys(newJobs).filter(
        (id) =>
          newJobs[id].status === "pending" ||
          newJobs[id].status === "running" ||
          newJobs[id].status === "completed"
      );
      writeStoredJobIds(accountId, idsToStore);

      // 4. Set state + start SSE for running jobs
      setJobs(newJobs);
      for (const jobId of validJobIds) {
        startSSE(jobId);
      }

      setIsInitializing(false);
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [accountId, closeAllSSE, startSSE]);

  // Cleanup on unmount
  useEffect(() => {
    return () => closeAllSSE();
  }, [closeAllSSE]);

  // -------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------

  const trackJob = useCallback(
    (jobId: string, dataSourceId: string) => {
      if (accountId) addStoredJobId(accountId, jobId);

      setJobs((prev) => ({
        ...prev,
        [jobId]: {
          jobId,
          dataSourceId,
          status: "pending",
          progressPct: 0,
          progressMessage: "Starting discovery...",
          tablesCompleted: 0,
          tablesTotal: 0,
          proposalId: null,
          error: null,
        },
      }));

      startSSE(jobId);
    },
    [accountId, startSSE]
  );

  const cancelJobAction = useCallback(
    async (jobId: string) => {
      try {
        await cancelDiscoveryJob(jobId);
        closeSSE(jobId);
        if (accountId) removeStoredJobId(accountId, jobId);
        setJobs((prev) => {
          const existing = prev[jobId];
          if (!existing) return prev;
          return {
            ...prev,
            [jobId]: { ...existing, status: "cancelled" },
          };
        });
      } catch (err) {
        console.error("Failed to cancel job:", err);
      }
    },
    [accountId, closeSSE]
  );

  const dismissJob = useCallback(
    (jobId: string) => {
      closeSSE(jobId);
      if (accountId) {
        removeStoredJobId(accountId, jobId);
        addDismissedJobId(accountId, jobId);
      }
      setJobs((prev) => {
        const { [jobId]: _, ...rest } = prev;
        return rest;
      });
    },
    [accountId, closeSSE]
  );

  const getJobForDataSource = useCallback(
    (dataSourceId: string): DiscoveryJobState | null => {
      // Find most recent active job for this data source
      const candidates = Object.values(jobs)
        .filter((j) => j.dataSourceId === dataSourceId)
        .filter(
          (j) =>
            j.status === "pending" ||
            j.status === "running" ||
            j.status === "completed"
        );
      return candidates[0] ?? null;
    },
    [jobs]
  );

  // -------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------

  const activeJobs = useMemo(
    () =>
      Object.values(jobs).filter(
        (j) => j.status === "pending" || j.status === "running"
      ),
    [jobs]
  );

  const completedJobs = useMemo(
    () =>
      Object.values(jobs).filter((j) => j.status === "completed"),
    [jobs]
  );

  const hasActiveJobs = activeJobs.length > 0;

  const value: DiscoveryJobContextValue = useMemo(
    () => ({
      jobs,
      isInitializing,
      trackJob,
      cancelJob: cancelJobAction,
      dismissJob,
      activeJobs,
      completedJobs,
      hasActiveJobs,
      isSetupSurfaceActive,
      setSetupSurfaceActive,
      getJobForDataSource,
    }),
    [
      jobs,
      isInitializing,
      trackJob,
      cancelJobAction,
      dismissJob,
      activeJobs,
      completedJobs,
      hasActiveJobs,
      isSetupSurfaceActive,
      getJobForDataSource,
    ]
  );

  return (
    <DiscoveryJobContext.Provider value={value}>
      {children}
    </DiscoveryJobContext.Provider>
  );
};

export const useDiscoveryJobContext = (): DiscoveryJobContextValue => {
  const context = useContext(DiscoveryJobContext);
  if (!context) {
    throw new Error(
      "useDiscoveryJobContext must be used within a DiscoveryJobProvider"
    );
  }
  return context;
};
